import {
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  Box,
  DialogActions
} from '@mui/material';
import NoteList from './NoteList';
import { useState } from 'react';
import useGetRequestNotes from '../hooks/useGetRequestNotes';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  GRAY_LIGHT_SECONDARY,
  GRAY_SECONDARY,
  PRIMARY,
  SECONDARY
} from 'constants/colors';
import classNames from 'classnames';
import dayjs from 'dayjs';
import KeyValueDisplay from './KeyValueDisplay';
import { calculateTimeDifference } from '../utils/calculateTimeDifference';
import FlexRequestHistoryTable from './FlexRequestHistoryTable';
import NotesBar from './NotesBar';

const StyledDialog = styled.div`
  min-width: 360px;
  padding: 10px;

  .MuiDialogTitle-root {
    padding: 10px;
    border-bottom: 1px solid ${GRAY_SECONDARY};
    margin-bottom: 10px;
  }

  .MuiDialogContent-root {
    padding: 0;
    margin-bottom: 0;
    font-size: 16px;
    font-family: 'signikaregular';
    white-space: pre-line;

    p {
      margin: 0;
    }
  }

  .MuiDialogTitle-root {
    font-size: 18px;
  }

  &.info {
    .MuiDialogTitle-root {
      border-color: ${PRIMARY};
      color: ${SECONDARY};
    }
  }
`;

const ColumnBox = styled(Box)`
  display: flex;
  flex-direction: column;
`;

export const DetailsDialog = ({ open, onClose, row }) => {
  const { t } = useTranslation();
  const requestId = row?.request?.requestId;
  const processInstanceId = row?.processInstanceId;
  const requestDate = dayjs(row?.request?.date).format('DD-MM-YYYY');
  const costCenter = row?.request?.costCenter?.name;
  const functionDescription = row?.request?.function?.description;
  const dutyCode = row?.request?.duty?.code;

  const dutyStartTime = row?.request?.duty?.dutyStartTime
    ? dayjs(row?.request?.duty?.dutyStartTime)
    : null;
  const dutyEndTime = row?.request?.duty?.dutyEndTime
    ? dayjs(row?.request?.duty?.dutyEndTime)
    : null;
  const formattedDutyStartTime = dayjs(dutyStartTime).format('HH:mm');
  const formattedDutyEndTime = dayjs(dutyEndTime).format('HH:mm');

  const totalTimeFormatted = calculateTimeDifference(
    dutyStartTime,
    dutyEndTime
  );

  const { data: notes, isFetching } = useGetRequestNotes(requestId);
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [showTaskHistory, setShowTaskHistory] = useState(true);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 800,
          maxWidth: 800
        }
      }}
    >
      <StyledDialog className={classNames('info')}>
        <DialogTitle id="confirmation-dialog">
          {t('flexHistory.flexRequestHistoryNr', { requestId })}
        </DialogTitle>
        <DialogContent>
          <Box
            display="flex"
            flexDirection="column"
            gap={2}
            maxWidth={800}
            margin="auto"
            px={1.25}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              gap={4}
              borderBottom={`1px solid ${GRAY_LIGHT_SECONDARY}`}
              pb={2}
            >
              <ColumnBox>
                <KeyValueDisplay label={t('date')} value={requestDate} />
                <KeyValueDisplay label={t('planUnit')} value={costCenter} />
                <KeyValueDisplay
                  label={t('function')}
                  value={functionDescription}
                />
              </ColumnBox>

              <ColumnBox>
                <KeyValueDisplay label={t('dutyCode')} value={dutyCode} />
                <KeyValueDisplay
                  label={t('period')}
                  value={
                    formattedDutyStartTime && formattedDutyEndTime
                      ? `${formattedDutyStartTime} - ${formattedDutyEndTime}`
                      : null
                  }
                />
                <KeyValueDisplay
                  label={t('flexHistory.totalTime')}
                  value={totalTimeFormatted}
                />
              </ColumnBox>
            </Box>

            <FlexRequestHistoryTable
              processInstanceId={processInstanceId}
              setShowTaskHistory={setShowTaskHistory}
              showTaskHistory={showTaskHistory}
            />

            <NotesBar
              requestId={requestId}
              showNotes={showNotes}
              setShowNotes={setShowNotes}
              showNoteForm={showNoteForm}
              setShowNoteForm={setShowNoteForm}
              notes={notes}
            />

            {showNotes && <NoteList notes={notes} isFetching={isFetching} />}
          </Box>

          <DialogActions>
            <Button onClick={onClose} color="dark" variant="contained">
              {t('cancel')}
            </Button>
          </DialogActions>
        </DialogContent>
      </StyledDialog>
    </Dialog>
  );
};
